// AttendeeList.tsx
/* eslint-disable */
import React, { useState } from 'react';
import { Registration, UpdateRegistrationInput } from './API';
import { updateRegistrationDetails } from './mutations/processUpdateRegistration';
import DownloadModal from './DownloadModal';
import Switch from "react-switch";
import { Select, Input, Checkbox, SearchIcon } from '@uclaea/react-component-library';
import './css/AttendeeList.css';
import AttendeeRow from './components/AttendeeRow';
import BulkActions from './components/BulkActions';
import PaginationControls from './components/PaginationControls';
import { formatDate } from './utils/formatDate';
import { parseAdditionalData, serializeAdditionalData } from './utils/parseAdditionalData';
import { calculateCheckInStatus } from './utils/registrationStatus';
import { useAttendeeFiltering } from './hooks/useAttendeeFiltering';
import { useAttendeeSelection } from './hooks/useAttendeeSelection';

interface AttendeeListProps {
  attendees: Registration[];
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>;
}

function AttendeeList(props: AttendeeListProps) {
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [rowActions, setRowActions] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const {
    setSearchQuery,
    testToggled, handleTestToggleChange,
    statusFilter, setStatusFilter,
    handleSort,
    currentPage, handleNextPage, handlePreviousPage,
    filteredAttendees, paginatedAttendees, totalPages,
  } = useAttendeeFiltering(props.attendees);

  const handleSubmit = async (newCheckedInCount: number, attendee: Registration) => {
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0),
    };
    try {
      const response = await updateRegistrationDetails(registrationData);
      if (response && response.checkedInCount === newCheckedInCount) {
        props.setAttendees(prevAttendees =>
          prevAttendees.map(a =>
            a.registrationId === attendee.registrationId
              ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus }
              : a
          )
        );
        setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
      } else {
        console.error('Unexpected response structure or status mismatch:', response);
      }
      setActiveDropdownId(null);
    } catch (err) {
      console.error('Error updating registration:', err);
      setActiveDropdownId(null);
    }
  };

  const {
    selectedAttendees,
    bulkAction, setBulkAction,
    showDownloadModal, setShowDownloadModal,
    selectedDownloadFormat, setSelectedDownloadFormat,
    toggleSelectAll, toggleSelectOne,
    handleBulkSubmit, handleDownloadConfirm,
  } = useAttendeeSelection(props.attendees, props.setAttendees, filteredAttendees, handleSubmit);

  const handleSaveRow = async (attendee: Registration & { notes?: string; membership?: string }) => {
    const additionalDataObj = parseAdditionalData(attendee.additionalData);
    const { membership, notes: attendeeNotes } = attendee;
    additionalDataObj.notes = notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendeeNotes || '';
    if (membership) {
      additionalDataObj.membership = membership;
    }
    const newAdditionalData = serializeAdditionalData(additionalDataObj);
    const newCheckedInCount = rowActions[attendee.registrationId] ?? attendee.checkedInCount;
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0),
      additionalData: newAdditionalData,
    };
    try {
      await updateRegistrationDetails(registrationData);
      props.setAttendees(prevAttendees =>
        prevAttendees.map(a =>
          a.registrationId === attendee.registrationId
            ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus, additionalData: newAdditionalData }
            : a
        )
      );
      setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
    } catch (err) {
      console.error('Error updating registration:', err);
    }
  };

  return (
    <>
      <div className='registrations-options-container'>
        <h2 className="registrations-options-header">Registrants</h2>
        <div className='registrations-status-container'>
          <p className='registrations-status-header-text'>Status:</p>
          <p className='registrations-status-value-text'>
            {props.attendees.filter((reg) => reg.registrationStatus === "CHECKED_IN" || reg.registrationStatus === "PARTIAL_CHECK_IN").length}/{props.attendees.length}
          </p>
        </div>
      </div>
      <div className='attendee-list-container'>
        <div className="toggle-row">
          <h4 className="attendee-header">Filter by Status</h4>
          <div className="filter-status-select">
            <Select
              label=""
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { label: 'All', id: 'all', value: 'ALL' },
                { label: 'Registered', id: 'registered', value: 'REGISTERED' },
                { label: 'Checked In', id: 'checked-in', value: 'CHECKED_IN' },
                { label: 'Partial Check-In', id: 'partial', value: 'PARTIAL_CHECK_IN' },
                { label: 'Cancelled', id: 'cancelled', value: 'CANCELLED' },
                { label: 'Invited', id: 'invited', value: 'INVITED' },
              ]}
            />
          </div>
        </div>
        <div className="toggle-row">
          <h4 className="attendee-header">Show Test Registrations</h4>
          <Switch onChange={handleTestToggleChange} checked={testToggled} />
        </div>
        <Input
          label="Search"
          type="text"
          placeholder="Search by name or email..."
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={{ left: SearchIcon }}
        />
        <BulkActions bulkAction={bulkAction} setBulkAction={setBulkAction} onSubmit={handleBulkSubmit} />
        <table className="attendee-table">
          <thead>
            <tr>
              <th>
                <Checkbox
                  label=""
                  onChange={e => toggleSelectAll((e.target as HTMLInputElement).checked)}
                  checked={selectedAttendees.size === filteredAttendees.length}
                  aria-label="Select all attendees"
                />
              </th>
              <th onClick={() => handleSort('lastName')}>Name</th>
              <th onClick={() => handleSort('email')}>Email</th>
              <th onClick={() => handleSort('dateCreated')} className='hide-for-mobile'>Transaction date</th>
              <th>Status</th>
              <th onClick={() => handleSort('guests')} className='hide-for-mobile'>Guests</th>
              <th onClick={() => handleSort('notes')} className='hide-for-mobile'>Notes</th>
              <th>Check In</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAttendees.map(attendee => (
              <AttendeeRow
                key={attendee.registrationId}
                attendee={attendee}
                selected={selectedAttendees.has(attendee.registrationId)}
                onSelect={() => toggleSelectOne(attendee.registrationId)}
                notesValue={notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendee.notes || ''}
                onNotesChange={val => setNotes(prev => ({ ...prev, [attendee.registrationId]: val }))}
                onSave={() => handleSaveRow(attendee)}
                rowAction={rowActions[attendee.registrationId] ?? attendee.checkedInCount}
                onRowActionChange={val => setRowActions({ ...rowActions, [attendee.registrationId]: val })}
                disabled={attendee.registrationStatus === 'CANCELLED'}
                formatDate={formatDate}
                activeDropdownId={activeDropdownId}
              />
            ))}
          </tbody>
        </table>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
        />
      </div>
      {showDownloadModal && (
        <DownloadModal
          selectedDownloadFormat={selectedDownloadFormat}
          setSelectedDownloadFormat={setSelectedDownloadFormat}
          onConfirm={handleDownloadConfirm}
          onCancel={() => setShowDownloadModal(false)}
        />
      )}
    </>
  );
}

export default AttendeeList;
